import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Building2, Calculator, Loader2, Save, FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useJobs } from "@/hooks/useJobs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { useAuth } from "@/hooks/useAuth";

const BASE_COST_PER_SQ_FT: Record<string, number> = {
  tpo: 3.50,
  pvc: 3.75,
  epdm: 3.25,
  modified_bitumen: 3.10,
  metal: 4.25,
};

const COSTS = {
  parapet_per_ft: 5.00,
  edge_metal_per_ft: 4.00,
  penetration_each: 75.00,
  drain_each: 150.00,
  hvac_each: 250.00,
};

interface FormData {
  membrane_type: string;
  total_square_feet: number;
  parapet_linear_feet: number;
  edge_metal_linear_feet: number;
  penetrations: number;
  drains: number;
  hvac_units: number;
}

interface LaborData {
  crew_size: number;
  labor_rate_per_hour: number;
  hours_per_day: number;
}

interface CalculationResults {
  material_cost_total: number;
  labor_cost_total: number;
  equipment_cost_total: number;
  overhead_percent: number;
  profit_percent: number;
  project_cost_total: number;
  cost_per_square: number;
  labor_days: number;
}

interface MeasurementOption {
  id: string;
  address: string;
  total_area: number;
  total_squares: number;
  eaves: number;
  rakes: number;
  created_at: string;
}

export default function Commercial() {
  const { jobs } = useJobs();
  const { user, orgId } = useAuth();
  const [selectedJobId, setSelectedJobId] = useState<string>("none");
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [selectedMeasurementId, setSelectedMeasurementId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [measurementModalOpen, setMeasurementModalOpen] = useState(false);
  const [measurements, setMeasurements] = useState<MeasurementOption[]>([]);
  const [loadingMeasurements, setLoadingMeasurements] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    membrane_type: "tpo",
    total_square_feet: 0,
    parapet_linear_feet: 0,
    edge_metal_linear_feet: 0,
    penetrations: 0,
    drains: 0,
    hvac_units: 0,
  });

  const [laborData, setLaborData] = useState<LaborData>({
    crew_size: 4,
    labor_rate_per_hour: 65,
    hours_per_day: 8,
  });

  const [overheadPercent, setOverheadPercent] = useState(15);
  const [profitPercent, setProfitPercent] = useState(20);
  const [results, setResults] = useState<CalculationResults | null>(null);

  useEffect(() => {
    if (selectedJobId && selectedJobId !== "none") {
      const job = jobs.find((j) => j.id === selectedJobId);
      setSelectedJob(job);
    } else {
      setSelectedJob(null);
    }
  }, [selectedJobId, jobs]);

  useEffect(() => {
    const importData = localStorage.getItem('commercialImport');
    if (importData) {
      try {
        const data = JSON.parse(importData);
        setFormData(prev => ({
          ...prev,
          total_square_feet: data.total_area || 0,
          edge_metal_linear_feet: (data.eaves || 0) + (data.rakes || 0),
        }));
        setSelectedMeasurementId(data.id || "");
        localStorage.removeItem('commercialImport');
        toast.success("Measurement data imported");
      } catch (error) {
        console.error("Error importing measurement:", error);
      }
    }
  }, []);

  const loadMeasurements = async () => {
    if (!orgId) return;
    
    setLoadingMeasurements(true);
    try {
      const { data, error } = await supabase
        .from("measurements_roof")
        .select("*")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setMeasurements(data || []);
    } catch (error: any) {
      console.error("Error loading measurements:", error);
      toast.error("Failed to load measurements");
    } finally {
      setLoadingMeasurements(false);
    }
  };

  const handlePullFromMeasurements = () => {
    loadMeasurements();
    setMeasurementModalOpen(true);
  };

  const handleSelectMeasurement = (measurement: MeasurementOption) => {
    setFormData(prev => ({
      ...prev,
      total_square_feet: measurement.total_area || 0,
      edge_metal_linear_feet: (measurement.eaves || 0) + (measurement.rakes || 0),
    }));
    setSelectedMeasurementId(measurement.id);
    setMeasurementModalOpen(false);
    toast.success("Measurement data loaded");
  };

  const calculateEstimate = () => {
    const { total_square_feet, parapet_linear_feet, edge_metal_linear_feet, penetrations, drains, hvac_units, membrane_type } = formData;
    const { crew_size, labor_rate_per_hour, hours_per_day } = laborData;

    const baseMaterialCost = total_square_feet * (BASE_COST_PER_SQ_FT[membrane_type] || 3.50);
    const parapetCost = parapet_linear_feet * COSTS.parapet_per_ft;
    const edgeMetalCost = edge_metal_linear_feet * COSTS.edge_metal_per_ft;
    const penetrationCost = penetrations * COSTS.penetration_each;
    const drainCost = drains * COSTS.drain_each;
    const hvacCost = hvac_units * COSTS.hvac_each;
    
    const material_cost_total = baseMaterialCost + parapetCost + edgeMetalCost + penetrationCost + drainCost + hvacCost;

    const baseProductivity = 1500;
    const scaledProductivity = (baseProductivity / 4) * crew_size;
    const labor_days = Math.ceil(total_square_feet / scaledProductivity);
    const labor_cost_total = labor_days * crew_size * labor_rate_per_hour * hours_per_day;

    const equipment_cost_total = 2500;

    const subtotal = material_cost_total + labor_cost_total + equipment_cost_total;
    const overhead = subtotal * (overheadPercent / 100);
    const profit = (subtotal + overhead) * (profitPercent / 100);
    const project_cost_total = subtotal + overhead + profit;

    const cost_per_square = total_square_feet > 0 ? project_cost_total / (total_square_feet / 100) : 0;

    setResults({
      material_cost_total,
      labor_cost_total,
      equipment_cost_total,
      overhead_percent: overheadPercent,
      profit_percent: profitPercent,
      project_cost_total,
      cost_per_square,
      labor_days,
    });

    toast.success("Estimate calculated");
  };

  const handleSaveEstimate = async () => {
    if (!results) {
      toast.error("Please calculate estimate first");
      return;
    }

    if (!orgId || !user) {
      toast.error("Authentication required");
      return;
    }

    setSaving(true);
    try {
      const address = selectedJob?.property_address || "Manual Entry";

      const { error: insertError } = await supabase
        .from("commercial_estimates")
        .insert({
          org_id: orgId,
          created_by: user.id,
          job_id: selectedJobId === "none" ? null : selectedJobId,
          measurement_id: selectedMeasurementId,
          address,
          membrane_type: formData.membrane_type,
          total_square_feet: formData.total_square_feet,
          parapet_linear_feet: formData.parapet_linear_feet,
          edge_metal_linear_feet: formData.edge_metal_linear_feet,
          penetrations: formData.penetrations,
          drains: formData.drains,
          hvac_units: formData.hvac_units,
          crew_size: laborData.crew_size,
          labor_rate_per_hour: laborData.labor_rate_per_hour,
          hours_per_day: laborData.hours_per_day,
          labor_days: results.labor_days,
          material_cost_total: results.material_cost_total,
          labor_cost_total: results.labor_cost_total,
          equipment_cost_total: results.equipment_cost_total,
          overhead_percent: results.overhead_percent,
          profit_percent: results.profit_percent,
          project_cost_total: results.project_cost_total,
          cost_per_square: results.cost_per_square,
        });

      if (insertError) throw insertError;

      if (selectedJobId && selectedJobId !== "none") {
        await supabase
          .from("jobs")
          .update({
            estimated_value: results.project_cost_total,
            notes: `Commercial estimate: $${results.project_cost_total.toLocaleString()}`,
          })
          .eq("id", selectedJobId);

        await supabase
          .from("job_timeline")
          .insert({
            org_id: orgId,
            job_id: selectedJobId,
            created_by: user.id,
            event_type: "estimate_created",
            description: "Commercial estimate created",
            event_data: { estimate_total: results.project_cost_total },
          });
      }

      toast.success("Estimate saved successfully!");
    } catch (error: any) {
      console.error("Error saving estimate:", error);
      toast.error(error.message || "Failed to save estimate");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="container mx-auto py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-foreground">Commercial Roofing Estimator</h1>
            <p className="text-muted-foreground mt-2">Professional estimation for commercial flat roofing projects</p>
          </div>
          <Badge variant="outline" className="text-lg px-4 py-2">
            <Building2 className="h-5 w-5 mr-2" />
            TPO / PVC / EPDM
          </Badge>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Select Job (Optional)</CardTitle>
            <CardDescription>Choose a job to associate with this estimate, or leave blank for standalone estimate</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Job</Label>
              <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a job (optional)..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None - Standalone Estimate</SelectItem>
                  {jobs.map((job) => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.job_number || "No Number"} - {job.property_address}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedJob && (
              <div className="p-3 bg-muted rounded-md">
                <p className="text-sm font-medium">Selected: {selectedJob.property_address}</p>
              </div>
            )}

            <Button onClick={handlePullFromMeasurements} variant="outline">
              <FileText className="mr-2 h-4 w-4" />
              Pull From Measurements
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Project Configuration</CardTitle>
            <CardDescription>Enter project specifications and requirements</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Membrane Type</Label>
                <Select
                  value={formData.membrane_type}
                  onValueChange={(value) => setFormData({ ...formData, membrane_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tpo">TPO ($3.50/sq ft)</SelectItem>
                    <SelectItem value="pvc">PVC ($3.75/sq ft)</SelectItem>
                    <SelectItem value="epdm">EPDM ($3.25/sq ft)</SelectItem>
                    <SelectItem value="modified_bitumen">Modified Bitumen ($3.10/sq ft)</SelectItem>
                    <SelectItem value="metal">Metal ($4.25/sq ft)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Total Square Feet</Label>
                <Input
                  type="number"
                  value={formData.total_square_feet || ""}
                  onChange={(e) => setFormData({ ...formData, total_square_feet: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div>
                <Label>Parapet Linear Feet</Label>
                <Input
                  type="number"
                  value={formData.parapet_linear_feet || ""}
                  onChange={(e) => setFormData({ ...formData, parapet_linear_feet: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div>
                <Label>Edge Metal (ft)</Label>
                <Input
                  type="number"
                  value={formData.edge_metal_linear_feet || ""}
                  onChange={(e) => setFormData({ ...formData, edge_metal_linear_feet: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div>
                <Label>Penetrations</Label>
                <Input
                  type="number"
                  value={formData.penetrations || ""}
                  onChange={(e) => setFormData({ ...formData, penetrations: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div>
                <Label>Drains</Label>
                <Input
                  type="number"
                  value={formData.drains || ""}
                  onChange={(e) => setFormData({ ...formData, drains: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div>
                <Label>HVAC Units</Label>
                <Input
                  type="number"
                  value={formData.hvac_units || ""}
                  onChange={(e) => setFormData({ ...formData, hvac_units: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div className="pt-4 border-t">
              <h3 className="font-semibold mb-3">Labor Configuration</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Crew Size</Label>
                  <Input
                    type="number"
                    value={laborData.crew_size || ""}
                    onChange={(e) => setLaborData({ ...laborData, crew_size: parseInt(e.target.value) || 4 })}
                  />
                </div>
                <div>
                  <Label>Labor Rate ($/hr)</Label>
                  <Input
                    type="number"
                    value={laborData.labor_rate_per_hour || ""}
                    onChange={(e) => setLaborData({ ...laborData, labor_rate_per_hour: parseFloat(e.target.value) || 65 })}
                  />
                </div>
                <div>
                  <Label>Hours Per Day</Label>
                  <Input
                    type="number"
                    value={laborData.hours_per_day || ""}
                    onChange={(e) => setLaborData({ ...laborData, hours_per_day: parseInt(e.target.value) || 8 })}
                  />
                </div>
              </div>
            </div>

            <div className="pt-4 border-t">
              <h3 className="font-semibold mb-3">Overhead & Profit</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Overhead (%)</Label>
                  <Input
                    type="number"
                    value={overheadPercent || ""}
                    onChange={(e) => setOverheadPercent(parseFloat(e.target.value) || 15)}
                  />
                </div>
                <div>
                  <Label>Profit Margin (%)</Label>
                  <Input
                    type="number"
                    value={profitPercent || ""}
                    onChange={(e) => setProfitPercent(parseFloat(e.target.value) || 20)}
                  />
                </div>
              </div>
            </div>

            <Button onClick={calculateEstimate} className="w-full" size="lg">
              <Calculator className="mr-2 h-5 w-5" />
              Calculate Estimate
            </Button>
          </CardContent>
        </Card>

        {results && (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Material Estimate</CardTitle>
                <CardDescription>Cost breakdown by component</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Base Material ({formData.total_square_feet.toLocaleString()} sq ft)</span>
                    <span className="font-semibold">${(formData.total_square_feet * (BASE_COST_PER_SQ_FT[formData.membrane_type] || 3.50)).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Parapet ({formData.parapet_linear_feet} ft @ $5/ft)</span>
                    <span className="font-semibold">${(formData.parapet_linear_feet * COSTS.parapet_per_ft).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Edge Metal ({formData.edge_metal_linear_feet} ft @ $4/ft)</span>
                    <span className="font-semibold">${(formData.edge_metal_linear_feet * COSTS.edge_metal_per_ft).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Penetrations ({formData.penetrations} @ $75 each)</span>
                    <span className="font-semibold">${(formData.penetrations * COSTS.penetration_each).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Drains ({formData.drains} @ $150 each)</span>
                    <span className="font-semibold">${(formData.drains * COSTS.drain_each).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">HVAC Units ({formData.hvac_units} @ $250 each)</span>
                    <span className="font-semibold">${(formData.hvac_units * COSTS.hvac_each).toLocaleString()}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-semibold">
                    <span>Material Total</span>
                    <span>${results.material_cost_total.toLocaleString()}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Labor Analysis</CardTitle>
                <CardDescription>Project timeline and labor costs</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Days Needed</p>
                    <p className="text-2xl font-bold">{results.labor_days} days</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Crew Size</p>
                    <p className="text-2xl font-bold">{laborData.crew_size} workers</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Labor Cost</p>
                    <p className="text-2xl font-bold">${results.labor_cost_total.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Equipment</p>
                    <p className="text-2xl font-bold">${results.equipment_cost_total.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-primary">
              <CardHeader>
                <CardTitle>Project Cost Summary</CardTitle>
                <CardDescription>Total investment breakdown</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Materials</span>
                    <span className="font-semibold">${results.material_cost_total.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Labor</span>
                    <span className="font-semibold">${results.labor_cost_total.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Equipment</span>
                    <span className="font-semibold">${results.equipment_cost_total.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Overhead ({results.overhead_percent}%)</span>
                    <span className="font-semibold">${((results.material_cost_total + results.labor_cost_total + results.equipment_cost_total) * (results.overhead_percent / 100)).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Profit ({results.profit_percent}%)</span>
                    <span className="font-semibold">${(((results.material_cost_total + results.labor_cost_total + results.equipment_cost_total) * (1 + results.overhead_percent / 100)) * (results.profit_percent / 100)).toLocaleString()}</span>
                  </div>
                  <div className="border-t pt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold">Total Estimate</span>
                      <span className="text-3xl font-bold text-primary">${results.project_cost_total.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between mt-2 text-sm text-muted-foreground">
                      <span>Cost Per Square</span>
                      <span>${results.cost_per_square.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button onClick={handleSaveEstimate} className="flex-1" disabled={saving}>
                      {saving ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="mr-2 h-4 w-4" />
                      )}
                      Save Estimate{selectedJobId ? " to Job" : ""}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <Dialog open={measurementModalOpen} onOpenChange={setMeasurementModalOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Select Measurement</DialogTitle>
            <DialogDescription>Choose a measurement to import data from</DialogDescription>
          </DialogHeader>
          
          {loadingMeasurements ? (
            <div className="py-12 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
            </div>
          ) : measurements.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-muted-foreground">No measurements found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Address</TableHead>
                  <TableHead>Area (sq ft)</TableHead>
                  <TableHead>Squares</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {measurements.map((measurement) => (
                  <TableRow key={measurement.id}>
                    <TableCell className="font-medium">{measurement.address}</TableCell>
                    <TableCell>{measurement.total_area?.toLocaleString()}</TableCell>
                    <TableCell>{measurement.total_squares?.toFixed(1)}</TableCell>
                    <TableCell>{format(new Date(measurement.created_at), "MMM d, yyyy")}</TableCell>
                    <TableCell>
                      <Button size="sm" onClick={() => handleSelectMeasurement(measurement)}>
                        Import
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
